/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Select, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useFormikContext, Field } from 'formik';
import { useDispatch } from 'react-redux';
import { Box } from 'atoms';
import FormField from './FormField';
import BulkUploadModal from '../../modals/BulkUploadModal';
import {
	addVendorServicabilityRouteV2,
	updateVendorServicabilityRouteV2,
	removeVendorServicabilityRouteV2,
	searchVendorServicabilityRoutesV2,
	bulkUploadServicabilityRoutesV2,
} from 'store/vendorSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import { getTripFilterList } from 'store/pageSlice';
import {
	HeaderContainer,
	SectionTitle,
	BulkUploadButton,
	StyledInput,
	StyledSelect,
	AddRouteButton,
	ViewButton,
	StyledTable,
} from './ServiceabilityInfoStep.styled';
import {
	validateEditingRoute,
	buildRouteForCreate,
	handleAPIResponse,
	handleUpdateRouteResponse,
	handleRemoveRouteResponse,
	getErrorHandler,
} from './ServiceabilityInfoStep.helpers';
import { MOVEMENT_TYPE_OPTIONS } from './ServiceabilityInfoStep.constants';
import RouteDetailsModal from './ServiceabilityInfoStep.modal';

const { Option } = Select;

export default function ServiceabilityInfoStep({
	isEditable = true,
	isDisabled = false,
	vendorId = null,
	isEditMode = false,
}) {
	const { values, setFieldValue } = useFormikContext();
	const dispatch = useDispatch();
	const disabled = !isEditable || isDisabled;
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedRoute, setSelectedRoute] = useState(null);
	const [addingRoute, setAddingRoute] = useState(false);
	const [editingRoute, setEditingRoute] = useState(null);

	// Dynamic city options state
	const [sourceCityOptions, setSourceCityOptions] = useState([]);
	const [destinationCityOptions, setDestinationCityOptions] = useState([]);
	const [loadingCities, setLoadingCities] = useState(false);

	// Bulk upload modal state
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
	const [bulkUploadFileKey, setBulkUploadFileKey] = useState(null);

	// Get routes from form values or use empty array
	const routes = values.routes || [];

	// Current route from form values
	const currentRoute = {
		vendor_name: values.vendor_name || '',
		type_of_movement: values.type_of_movement || '',
		source_city: values.source_city || '',
		destination_city: values.destination_city || '',
	};

	/**
	 * Fetch cities from v1 API
	 * Uses getTripFilterList('city') to fetch all available cities
	 */
	const fetchCities = useCallback(async () => {
		setLoadingCities(true);
		try {
			console.log('🔍 [ServiceabilityInfoStep] Fetching cities from v1 API');

			const response = await dispatch(getTripFilterList('city'));

			console.log('✅ [ServiceabilityInfoStep] Cities response:', response);

			const cities = response?.rows?.cities || [];
			const cityOptions = cities.map((city) => ({
				value: city,
				label: city,
			}));

			// Use same city list for both source and destination
			setSourceCityOptions(cityOptions);
			setDestinationCityOptions(cityOptions);

			console.log('🏙️ [ServiceabilityInfoStep] Cities loaded:', {
				count: cityOptions.length,
			});
		} catch (error) {
			console.error('❌ [ServiceabilityInfoStep] Failed to fetch cities:', error);
			message.error('Failed to load cities. Please try again.');
			// Fallback to empty arrays
			setSourceCityOptions([]);
			setDestinationCityOptions([]);
		} finally {
			setLoadingCities(false);
		}
	}, [dispatch]);

	/**
	 * Fetch routes for vendor in edit mode
	 */
	const fetchVendorRoutes = useCallback(async () => {
		if (!isEditMode || !vendorId) {
			return;
		}

		try {
			console.log('🔄 [ServiceabilityInfoStep] Fetching routes for vendor:', vendorId);
			const response = await dispatch(
				searchVendorServicabilityRoutesV2({
					filter_by: { vendor_id: vendorId, is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			);

			if (response?.routes && Array.isArray(response.routes)) {
				console.log('✅ [ServiceabilityInfoStep] Routes fetched:', response.routes.length);
				// Transform routes to form format
				const transformedRoutes = response.routes.map((route) => ({
					key: route.id?.toString() || `route_${Date.now()}`,
					id: route.id,
					vendorName: route.vendor_name || values.vendor_name || '',
					typeOfMovement: route.type_of_movement || '',
					sourceCity: route.source_city || '',
					destinationCity: route.destination_city || '',
				}));
				setFieldValue('routes', transformedRoutes);
			} else {
				console.log('⚠️ [ServiceabilityInfoStep] No routes found or invalid response');
				setFieldValue('routes', []);
			}
		} catch (error) {
			console.error('❌ [ServiceabilityInfoStep] Failed to fetch routes:', error);
			message.error('Failed to load routes. Please try again.');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, vendorId, dispatch, setFieldValue]);

	/**
	 * Initialize component - clear input fields and fetch cities
	 */
	useEffect(() => {
		// Clear route input fields on mount to ensure clean state
		// These fields are for adding new routes, so they should always start empty
		setFieldValue('type_of_movement', '');
		setFieldValue('source_city', '');
		setFieldValue('destination_city', '');

		// Fetch cities
		fetchCities();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty dependency array - run only once on mount

	/**
	 * Fetch routes when entering edit mode or when vendorId changes
	 */
	useEffect(() => {
		if (isEditMode && vendorId) {
			fetchVendorRoutes();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditMode, vendorId, fetchVendorRoutes]);

	/**
	 * Handle type_of_movement changes for intracity logic
	 * - When changing from "Intracity" to "Intercity", clear destination city
	 * - When changing to "Intracity" and source city exists, auto-fill destination city
	 */
	useEffect(() => {
		const typeOfMovement = values.type_of_movement;
		const sourceCity = values.source_city;
		const destinationCity = values.destination_city;

		if (typeOfMovement === 'Intracity' && sourceCity && destinationCity !== sourceCity) {
			// If source city is selected, auto-fill destination city
			setFieldValue('destination_city', sourceCity);
		} else if (typeOfMovement === 'Intercity' && destinationCity === sourceCity) {
			// If switching from Intracity to Intercity, clear destination city
			// Only clear if destination was previously set to source (intracity scenario)
			setFieldValue('destination_city', '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.type_of_movement, values.source_city]);

	/**
	 * Note: Cities are fetched once on mount for both intercity and intracity
	 * No need to re-fetch when type_of_movement changes since we fetch all cities
	 */

	/**
	 * Clear route input fields
	 */
	const clearRouteFields = () => {
		setFieldValue('type_of_movement', '');
		setFieldValue('source_city', '');
		setFieldValue('destination_city', '');
	};

	/**
	 * Add route via API (edit mode)
	 */
	const addRouteViaAPI = async () => {
		setAddingRoute(true);
		try {
			const response = await dispatch(
				addVendorServicabilityRouteV2({
					vendor_id: vendorId,
					type_of_movement: currentRoute.type_of_movement,
					source_city: currentRoute.source_city,
					destination_city: currentRoute.destination_city,
					setErrors: getErrorHandler(),
				})
			);
			const success = handleAPIResponse(response, routes, setFieldValue, currentRoute);
			if (success) {
				clearRouteFields();
				// Refresh routes after adding in edit mode
				if (isEditMode && vendorId) {
					await fetchVendorRoutes();
				}
			}
		} catch (error) {
			message.error(error.message || 'Failed to add route');
		} finally {
			setAddingRoute(false);
		}
	};

	/**
	 * Add route to local state (create mode)
	 */
	const addRouteToLocalState = () => {
		const newRoute = buildRouteForCreate(currentRoute);
		const updatedRoutes = [...routes, newRoute];
		setFieldValue('routes', updatedRoutes);
		clearRouteFields();
	};

	const handleAddRoute = async () => {
		// Manual validation for route input fields
		if (!currentRoute.type_of_movement) {
			message.error('Type of Movement is required to add a route');
			return;
		}

		if (!currentRoute.source_city) {
			message.error('Source City is required to add a route');
			return;
		}

		if (!currentRoute.destination_city) {
			message.error('Destination City is required to add a route');
			return;
		}

		// Check if source and destination are the same (only for Intercity, not Intracity)
		if (
			currentRoute.type_of_movement === 'Intercity' &&
			currentRoute.source_city === currentRoute.destination_city
		) {
			message.error('Source City and Destination City cannot be the same');
			return;
		}

		// In edit mode with vendor_id, call API
		if (isEditMode && vendorId) {
			await addRouteViaAPI();
		} else {
			// Create mode - add to local state
			addRouteToLocalState();
		}
	};

	const handleViewRoute = (route) => {
		setSelectedRoute(route);
		setEditingRoute({
			typeOfMovement: route.typeOfMovement || '',
			sourceCity: route.sourceCity || '',
			destinationCity: route.destinationCity || '',
		});
		setModalVisible(true);
	};

	const handleModalClose = () => {
		setModalVisible(false);
		setSelectedRoute(null);
		setEditingRoute(null);
	};

	/**
	 * Update route in create mode (local state only)
	 */
	const updateRouteInCreateMode = (routeToUpdate, routeData) => {
		const updatedRoutes = routes.map((route) =>
			route.key === routeToUpdate.key || route.id === routeToUpdate.id
				? {
						...route,
						typeOfMovement: routeData.typeOfMovement,
						sourceCity: routeData.sourceCity,
						destinationCity: routeData.destinationCity,
				  }
				: route
		);
		setFieldValue('routes', updatedRoutes);
		message.success('Route updated successfully');
		handleModalClose();
	};

	/**
	 * Update route via API
	 */
	const handleSaveChanges = async () => {
		if (!selectedRoute) {
			message.error('No route selected');
			return;
		}

		if (!editingRoute) {
			message.error('No changes to save');
			return;
		}

		if (!validateEditingRoute(editingRoute)) {
			return;
		}

		// Check if source and destination are the same (only for Intercity, not Intracity)
		if (
			editingRoute.typeOfMovement === 'Intercity' &&
			editingRoute.sourceCity === editingRoute.destinationCity
		) {
			message.error('Source City and Destination City cannot be the same');
			return;
		}

		// In edit mode with route ID, call API
		if (isEditMode && vendorId && selectedRoute.id) {
			setAddingRoute(true);
			try {
				const response = await dispatch(
					updateVendorServicabilityRouteV2({
						id: selectedRoute.id,
						type_of_movement: editingRoute.typeOfMovement,
						source_city: editingRoute.sourceCity,
						destination_city: editingRoute.destinationCity,
						is_active: true,
						setErrors: getErrorHandler(),
					})
				);
				const success = handleUpdateRouteResponse(
					response,
					routes,
					selectedRoute,
					editingRoute,
					setFieldValue,
					handleModalClose
				);
				// Refresh routes after update
				if (success && isEditMode && vendorId) {
					await fetchVendorRoutes();
				}
			} catch (error) {
				message.error(error.message || 'Failed to update route');
			} finally {
				setAddingRoute(false);
			}
		} else {
			// In create mode, just update local state
			updateRouteInCreateMode(selectedRoute, editingRoute);
		}
	};

	/**
	 * Remove route via API (soft delete)
	 */
	const handleRemoveRoute = async () => {
		if (!selectedRoute) {
			message.error('No route selected');
			return;
		}

		// In edit mode, call API to remove
		if (isEditMode && vendorId && selectedRoute.id) {
			setAddingRoute(true);
			try {
				const response = await dispatch(
					removeVendorServicabilityRouteV2({
						id: selectedRoute.id,
						setErrors: getErrorHandler(),
					})
				);
				const success = handleRemoveRouteResponse(
					response,
					routes,
					selectedRoute,
					setFieldValue,
					handleModalClose
				);
				// Refresh routes after removal
				if (success && isEditMode && vendorId) {
					await fetchVendorRoutes();
				}
			} catch (error) {
				message.error(error.message || 'Failed to remove route');
			} finally {
				setAddingRoute(false);
			}
		} else {
			// In create mode, just remove from local state
			const updatedRoutes = routes.filter(
				(route) =>
					route.key !== selectedRoute.key &&
					(!selectedRoute.id || route.id !== selectedRoute.id)
			);
			setFieldValue('routes', updatedRoutes);
			message.success('Route removed successfully');
			handleModalClose();
		}
	};

	/**
	 * Open bulk upload modal
	 */
	const handleBulkUpload = () => {
		setBulkUploadModalVisible(true);
	};

	/**
	 * Handle file upload to S3 for bulk upload
	 * @param {File} file - CSV file to upload
	 */
	const handleBulkUploadFileUpload = async (file) => {
		console.log('📁 [ServiceabilityInfoStep] Starting bulk upload file upload to S3:', file);

		try {
			// Generate S3 key with timestamp
			const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
			const fileName = `vendor_servicability_routes_${timestamp}.csv`;

			// Step 1: Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vendor_servicability_route',
						keys: [fileName],
					},
				})
			);

			console.log('✅ [ServiceabilityInfoStep] Signed URL response:', signedUrlResponse);

			// Extract the signed URL and key from response
			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Step 2: Upload file to S3 using fetch
			const uploadResponse = await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type || 'text/csv',
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(`Upload failed with status: ${uploadResponse.status}`);
			}

			console.log('✅ [ServiceabilityInfoStep] File uploaded to S3 successfully');

			// Store the file key (the filename returned from API) for submission
			setBulkUploadFileKey(fileData.filename);
			message.success('File uploaded successfully');
		} catch (error) {
			console.error('❌ [ServiceabilityInfoStep] File upload failed:', error);
			message.error('Failed to upload file. Please try again.');
			throw error;
		}
	};

	/**
	 * Handle bulk upload submission
	 */
	const handleBulkUploadSubmit = async () => {
		console.log(
			'📤 [ServiceabilityInfoStep] Submitting bulk upload with file key:',
			bulkUploadFileKey
		);

		if (!bulkUploadFileKey) {
			message.error('Please upload a file first');
			return;
		}

		setBulkUploadLoading(true);

		try {
			const response = await dispatch(
				bulkUploadServicabilityRoutesV2({
					fileKey: bulkUploadFileKey,
					entity: 'vendor_servicability_route',
					setErrors: (uploadErrors) => {
						console.error(
							'❌ [ServiceabilityInfoStep] Bulk upload errors:',
							uploadErrors
						);
						message.error(uploadErrors?.message || 'Bulk upload failed');
					},
				})
			);

			console.log('✅ [ServiceabilityInfoStep] Bulk upload response:', response);

			if (response?.status === 'success') {
				// Close modal
				setBulkUploadModalVisible(false);
				setBulkUploadFileKey(null);

				// Download result CSV if available
				if (response.data?.download_url || response.download_url) {
					const downloadUrl = response.data?.download_url || response.download_url;
					console.log('⬇️ [ServiceabilityInfoStep] Downloading result CSV:', downloadUrl);
					window.open(downloadUrl, '_blank');
				}

				// Show success message
				const { success_count, error_count, total_count } = response.data || {};
				message.success(
					`Bulk upload completed: ${success_count} success, ${error_count} errors out of ${total_count} total`
				);

				// Refresh routes list if in edit mode
				if (isEditMode && vendorId) {
					fetchCities();
				}
			}
		} catch (error) {
			console.error('❌ [ServiceabilityInfoStep] Bulk upload submission failed:', error);
			message.error('Bulk upload failed. Please try again.');
		} finally {
			setBulkUploadLoading(false);
		}
	};

	/**
	 * Handle modal close
	 */
	const handleBulkUploadModalClose = () => {
		setBulkUploadModalVisible(false);
		setBulkUploadFileKey(null);
		setBulkUploadLoading(false);
	};

	const columns = [
		{
			title: 'Vendor Name',
			dataIndex: 'vendorName',
			key: 'vendorName',
		},
		{
			title: 'Type of Movement',
			dataIndex: 'typeOfMovement',
			key: 'typeOfMovement',
		},
		{
			title: 'Source City',
			dataIndex: 'sourceCity',
			key: 'sourceCity',
		},
		{
			title: 'Destination City',
			dataIndex: 'destinationCity',
			key: 'destinationCity',
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_, record) => (
				<ViewButton
					icon={<EyeOutlined style={{ fontSize: '18px' }} />}
					onClick={() => handleViewRoute(record)}
				/>
			),
		},
	];

	return (
		<Box>
			<HeaderContainer>
				<div />
				<BulkUploadButton onClick={handleBulkUpload} disabled={disabled}>
					Bulk Upload
				</BulkUploadButton>
			</HeaderContainer>

			<Row gutter={24}>
				<Col span={12}>
					<FormField label="Vendor Name" required>
						<Field name="vendor_name">
							{({ field }) => (
								<StyledInput
									{...field}
									value={values.vendor_name || ''}
									disabled={disabled || true}
								/>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={12}>
					<FormField label="Type of Movement" required>
						<Field name="type_of_movement">
							{({ field, form }) => (
								<StyledSelect
									{...field}
									placeholder="Select"
									disabled={disabled}
									value={values.type_of_movement}
									onChange={(value) => {
										form.setFieldValue('type_of_movement', value);
									}}
								>
									{MOVEMENT_TYPE_OPTIONS.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</StyledSelect>
							)}
						</Field>
					</FormField>
				</Col>
			</Row>

			<Row gutter={24}>
				<Col span={12}>
					<FormField label="Source City" required>
						<Field name="source_city">
							{({ field, form }) => (
								<StyledSelect
									{...field}
									placeholder="Select"
									disabled={disabled || loadingCities}
									value={values.source_city}
									onChange={(value) => {
										form.setFieldValue('source_city', value);
										// Auto-fill destination city when type is Intracity
										if (values.type_of_movement === 'Intracity' && value) {
											form.setFieldValue('destination_city', value);
										}
									}}
									showSearch
									loading={loadingCities}
									filterOption={(input, option) =>
										(option?.children || '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									notFoundContent={
										loadingCities ? 'Loading cities...' : 'No cities found'
									}
								>
									{sourceCityOptions.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</StyledSelect>
							)}
						</Field>
					</FormField>
				</Col>
				<Col span={12}>
					<FormField label="Destination City" required>
						<Field name="destination_city">
							{({ field, form }) => (
								<StyledSelect
									{...field}
									placeholder="Select"
									disabled={
										disabled ||
										loadingCities ||
										(values.type_of_movement === 'Intracity' &&
											values.source_city)
									}
									value={values.destination_city}
									onChange={(value) => {
										form.setFieldValue('destination_city', value);
									}}
									showSearch
									loading={loadingCities}
									filterOption={(input, option) =>
										(option?.children || '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									notFoundContent={
										loadingCities ? 'Loading cities...' : 'No cities found'
									}
								>
									{destinationCityOptions.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</StyledSelect>
							)}
						</Field>
					</FormField>
				</Col>
			</Row>

			<Box style={{ textAlign: 'center' }}>
				<AddRouteButton
					disabled={disabled || addingRoute}
					loading={addingRoute}
					onClick={() => !disabled && !addingRoute && handleAddRoute()}
				>
					Add Route
				</AddRouteButton>
			</Box>

			<SectionTitle style={{ marginTop: '48px', marginBottom: '20px' }}>
				Serviceable Route Details
			</SectionTitle>

			<StyledTable columns={columns} dataSource={routes} pagination={false} />

			{/* Route Details Modal */}
			<RouteDetailsModal
				visible={modalVisible}
				selectedRoute={selectedRoute}
				editingRoute={editingRoute}
				setEditingRoute={setEditingRoute}
				disabled={disabled}
				addingRoute={addingRoute}
				onClose={handleModalClose}
				onSave={handleSaveChanges}
				onRemove={handleRemoveRoute}
				sourceCityOptions={sourceCityOptions}
				destinationCityOptions={destinationCityOptions}
				loadingCities={loadingCities}
			/>

			{/* Bulk Upload Modal */}
			<BulkUploadModal
				visible={bulkUploadModalVisible}
				onClose={handleBulkUploadModalClose}
				onSubmit={handleBulkUploadSubmit}
				onFileUpload={handleBulkUploadFileUpload}
				loading={bulkUploadLoading}
				title="Bulk Upload Serviceability Routes"
				description="Please upload a CSV file to add multiple serviceability routes at once. The vendor name in the CSV must match the vendor being created/edited."
				acceptedFormats=".csv"
				templateText="Download template:"
			/>
		</Box>
	);
}

ServiceabilityInfoStep.propTypes = {
	isEditable: PropTypes.bool,
	isDisabled: PropTypes.bool,
	vendorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	isEditMode: PropTypes.bool,
};
